import React, { useState, useMemo } from 'react';
import useWindowWidth from '../hooks/useWindowWidth';
import { medalColor } from './Podium';

/**
 * RankingTable — a sortable, paginated, dense table for Box-Office-Mojo-style
 * ranking views. Shares the visual language of DataTable.js (neumorphic card,
 * sticky uppercase header, hover rows) but adds:
 *   - click-to-sort headers (asc/desc) with an indicator
 *   - optional pagination (pageSize)
 *   - reusable "movement" cell renderers (WoW % pill, LW-rank arrows)
 *
 * Column shape (extends DataTable's):
 *   { key, header, align, render(row), sortable, sortValue(row) }
 *   - sortable: default true; set false to disable sorting on that column
 *   - sortValue: value used for sorting (defaults to row[key]); use for cells
 *     whose render() is decorative (e.g. a movement pill)
 */

const num = (v) => (typeof v === 'number' ? v : (v == null ? -Infinity : v));

function compareVals(a, b) {
  if (typeof a === 'number' || typeof b === 'number') return num(a) - num(b);
  return String(a ?? '').localeCompare(String(b ?? ''), 'he');
}

function RankingTable({
  columns,
  data,
  onRowClick,
  getRowStyle,
  pageSize,
  initialSort,        // { key, dir: 'asc' | 'desc' }
  minWidth = 700,
  maxHeight,
  rankColumn = false, // prepend an automatic 1-based "#" rank column
  mobileStatCols = 3, // columns of the stat grid on a mobile card
}) {
  const [sort, setSort] = useState(initialSort || null);
  const [page, setPage] = useState(0);
  const mob = useWindowWidth() < 768;

  const sorted = useMemo(() => {
    if (!sort) return data;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return data;
    const valOf = col.sortValue || (row => row[col.key]);
    const arr = [...data].sort((ra, rb) => compareVals(valOf(ra), valOf(rb)));
    if (sort.dir === 'desc') arr.reverse();
    return arr;
  }, [data, sort, columns]);

  const paged = useMemo(() => {
    if (!pageSize) return sorted;
    return sorted.slice(page * pageSize, page * pageSize + pageSize);
  }, [sorted, page, pageSize]);

  const pageCount = pageSize ? Math.ceil(sorted.length / pageSize) : 1;

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setPage(0);
    setSort(prev => {
      if (!prev || prev.key !== col.key) return { key: col.key, dir: 'desc' };
      if (prev.dir === 'desc') return { key: col.key, dir: 'asc' };
      return null; // third click clears sort
    });
  };

  const thBase = (align) => ({
    padding: '12px 16px',
    textAlign: align || 'right',
    color: 'var(--faint)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    ...(maxHeight ? { position: 'sticky', top: 0, zIndex: 2, background: 'var(--surf-alt)' } : {}),
  });

  // ── Mobile: card list instead of a horizontally-scrolling table ──────────
  if (mob) {
    // The entity column (title/name/date) is the right-aligned one; it becomes
    // the card header. The primary metric (mobilePrimary flag, or the tickets
    // column) is promoted next to it; wide cells (mobileWide) get a full-width
    // row; everything else goes into the stat grid.
    const titleCol = columns.find(c => c.align === 'right') || columns[0];
    const rest = columns.filter(c => c !== titleCol);
    const primaryCol = rest.find(c => c.mobilePrimary)
      || rest.find(c => c.key === 'tickets' || c.key === 'total_tickets')
      || null;
    const wideCols = rest.filter(c => c.mobileWide && c !== primaryCol);
    const statCols = rest.filter(c => c !== primaryCol && !c.mobileWide);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Sort chips */}
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2, WebkitOverflowScrolling: 'touch' }}>
          {columns.filter(c => c.sortable !== false).map((col, i) => {
            const active = sort && sort.key === col.key;
            return (
              <button key={i} onClick={() => toggleSort(col)} style={{
                padding: '5px 11px', borderRadius: 20, fontSize: 11.5, whiteSpace: 'nowrap',
                fontFamily: 'Heebo, sans-serif', fontWeight: active ? 700 : 400, cursor: 'pointer',
                border: `1px solid ${active ? 'var(--accent)' : 'var(--brd)'}`,
                background: active ? 'var(--accent)' : 'var(--surf)', color: active ? '#fff' : 'var(--muted)',
                flexShrink: 0,
              }}>
                {col.header} {active ? (sort.dir === 'desc' ? '▾' : '▴') : ''}
              </button>
            );
          })}
        </div>

        {paged.map((row, rowIdx) => {
          const rank = (pageSize ? page * pageSize : 0) + rowIdx + 1;
          return (
            <MobileCard
              key={row.movie_id ?? row.id ?? rowIdx}
              row={row} rank={rank} titleCol={titleCol} statCols={statCols}
              primaryCol={primaryCol} wideCols={wideCols} gridCols={mobileStatCols}
              showRank={rankColumn} extra={getRowStyle ? getRowStyle(row) : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            />
          );
        })}

        {pageSize && pageCount > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
            padding: '10px 0', fontSize: 12, color: 'var(--muted)',
          }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              style={pagerBtn(page === 0)}>‹ הקודם</button>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{page + 1} / {pageCount}</span>
            <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
              style={pagerBtn(page >= pageCount - 1)}>הבא ›</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--surf)',
      borderRadius: 16,
      boxShadow: 'var(--shadow)',
      border: '1px solid var(--brd)',
      overflow: 'hidden',
    }}>
      <div style={{ overflowX: 'auto', overflowY: maxHeight ? 'auto' : 'visible', maxHeight }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth }}>
          <thead>
            <tr style={{ background: 'var(--surf-alt)', borderBottom: '1px solid var(--brd)' }}>
              {rankColumn && <th style={{ ...thBase('center'), width: 44 }}>#</th>}
              {columns.map((col, i) => {
                const active = sort && sort.key === col.key;
                const sortable = col.sortable !== false;
                return (
                  <th
                    key={i}
                    onClick={() => toggleSort(col)}
                    style={{ ...thBase(col.align), cursor: sortable ? 'pointer' : 'default', userSelect: 'none' }}
                  >
                    {col.header}
                    {sortable && (
                      <span style={{ marginInlineStart: 4, color: active ? 'var(--accent)' : 'var(--brd)' }}>
                        {active ? (sort.dir === 'desc' ? '▾' : '▴') : '⇅'}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paged.map((row, rowIdx) => {
              const extra = getRowStyle ? getRowStyle(row) : undefined;
              const baseBg = extra?.background || 'transparent';
              const rank = (pageSize ? page * pageSize : 0) + rowIdx + 1;
              return (
                <tr
                  key={row.movie_id ?? row.id ?? rowIdx}
                  onClick={() => onRowClick && onRowClick(row)}
                  style={{
                    borderBottom: '1px solid var(--brd)',
                    cursor: onRowClick ? 'pointer' : 'default',
                    transition: 'background 0.1s',
                    ...extra,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--surf-hov)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = baseBg; }}
                >
                  {rankColumn && (
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      color: rank <= 3 ? medalColor(rank - 1) : 'var(--faint)', fontSize: rank <= 3 ? 14 : 12 }}>
                      {rank}
                    </td>
                  )}
                  {columns.map((col, colIdx) => (
                    <td key={colIdx} style={{
                      padding: '12px 16px',
                      textAlign: col.align || 'right',
                      fontSize: 13,
                      color: 'var(--txt)',
                      whiteSpace: 'nowrap',
                    }}>
                      {col.render ? col.render(row, rank) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pageSize && pageCount > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
          padding: '12px 16px', borderTop: '1px solid var(--brd)', fontSize: 12, color: 'var(--muted)',
        }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            style={pagerBtn(page === 0)}>‹ הקודם</button>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{page + 1} / {pageCount}</span>
          <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
            style={pagerBtn(page >= pageCount - 1)}>הבא ›</button>
        </div>
      )}
    </div>
  );
}

// One movie/entity card for the mobile layout: rank + title + the primary
// metric up top, secondary stats in a light grid, wide cells (posters etc.)
// on their own full-width rows — so no data is lost and hierarchy is clear.
function MobileCard({ row, rank, titleCol, statCols, primaryCol, wideCols, gridCols = 3, showRank, extra, onClick }) {
  const [pressed, setPressed] = useState(false);
  return (
    <div
      onClick={onClick}
      onTouchStart={() => setPressed(true)} onTouchEnd={() => setPressed(false)}
      onMouseDown={() => setPressed(true)} onMouseUp={() => setPressed(false)} onMouseLeave={() => setPressed(false)}
      style={{
        background: 'var(--surf)', border: '1px solid var(--brd)', borderRadius: 14,
        padding: 14, boxShadow: 'var(--shadow-sm)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform .08s ease', transform: pressed ? 'scale(0.985)' : 'none',
        ...extra,
      }}
    >
      {/* Header: rank + entity + promoted primary metric */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {showRank && (
          <div style={{
            width: 24, flexShrink: 0, textAlign: 'center', fontWeight: 800,
            fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5,
            fontSize: rank <= 3 ? 18 : 14, color: rank <= 3 ? medalColor(rank - 1) : 'var(--faint)',
          }}>{rank}</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {titleCol.render ? titleCol.render(row, rank) : row[titleCol.key]}
        </div>
        {primaryCol && (
          <div style={{ flexShrink: 0, textAlign: 'center', paddingRight: 6 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent)', letterSpacing: -0.5, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
              {typeof row[primaryCol.key] === 'number' ? row[primaryCol.key].toLocaleString() : (primaryCol.render ? primaryCol.render(row, rank) : row[primaryCol.key])}
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--faint)', letterSpacing: 0.3, marginTop: 2 }}>{primaryCol.header}</div>
          </div>
        )}
      </div>

      {statCols.length > 0 && <div style={{ height: 1, background: 'var(--brd)', margin: '12px 0 10px' }} />}

      {/* Secondary stat grid */}
      {statCols.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${gridCols}, 1fr)`, gap: '12px 6px' }}>
          {statCols.map((col, i) => (
            <div key={i} style={{ textAlign: 'center', minWidth: 0 }}>
              <div style={{
                fontSize: 9, fontWeight: 700, color: 'var(--faint)', letterSpacing: 0.3,
                marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{col.header}</div>
              <div style={{ fontSize: 13, color: 'var(--txt)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: 28 }}>
                {col.render ? col.render(row, rank) : (row[col.key] ?? '—')}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Wide cells: full-width rows (posters, complex content) */}
      {wideCols.map((col, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 12,
          borderTop: '1px solid var(--brd)', marginTop: 10, paddingTop: 10,
        }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--faint)', letterSpacing: 0.3, flexShrink: 0 }}>{col.header}</span>
          <div style={{ minWidth: 0, flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
            {col.render ? col.render(row, rank) : (row[col.key] ?? '—')}
          </div>
        </div>
      ))}
    </div>
  );
}

const pagerBtn = (disabled) => ({
  padding: '5px 12px', borderRadius: 8, border: '1px solid var(--brd)',
  background: 'var(--surf)', color: disabled ? 'var(--faint)' : 'var(--txt)',
  cursor: disabled ? 'default' : 'pointer', fontFamily: 'Heebo, sans-serif', fontSize: 12,
  opacity: disabled ? 0.5 : 1,
});

/* ── Reusable movement renderers ──────────────────────────────────────────
   Shared by every BOM-style ranking view so the WoW/LW visuals stay consistent. */

// Week-over-week percent pill. prev<=0/null → "סרט חדש!" badge.
export function WoWCell({ curr, prev }) {
  const chg = prev ? Math.round((curr - prev) * 100 / prev) : null;
  if (chg == null) {
    return (
      <span style={{ fontSize: 10, fontWeight: 700, color: '#ca8a04',
        background: 'rgba(234,179,8,0.18)', padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>
        סרט חדש!
      </span>
    );
  }
  const up = chg >= 0;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: up ? 'var(--ok)' : 'var(--danger)' }}>
        {up ? '↑' : '↓'}{Math.abs(chg)}%
      </span>
      <span style={{ fontSize: 9.5, color: 'var(--faint)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {prev.toLocaleString()} כרטיסים
      </span>
    </span>
  );
}
// Numeric sort value for a WoW column.
WoWCell.sortValue = (curr, prev) => (prev ? (curr - prev) * 100 / prev : Infinity);

// LW-rank movement: arrows vs last week's rank. lastWeekRank null → new entry.
export function RankMovementCell({ currentRank, lastWeekRank }) {
  if (lastWeekRank == null) {
    return <span style={{ fontSize: 11, fontWeight: 700, color: '#ca8a04' }}>חדש</span>;
  }
  const delta = lastWeekRank - currentRank; // positive = climbed
  if (delta === 0) return <span style={{ color: 'var(--faint)' }}>– {lastWeekRank}</span>;
  const up = delta > 0;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: up ? 'var(--ok)' : 'var(--danger)' }}>
      {up ? '▲' : '▼'}{Math.abs(delta)} <span style={{ color: 'var(--faint)', fontWeight: 400 }}>({lastWeekRank})</span>
    </span>
  );
}

export default RankingTable;
